import React, { useEffect, useRef } from 'react';
import ScrollToTop from '@site/src/components/ScrollToTop';

const COPYABLE_CODE_SELECTOR = 'code';

function isInteractiveAncestor(code) {
  return Boolean(code.closest('a, button, input, textarea, select, [contenteditable="true"], [data-no-code-copy]'));
}

function enhanceCode(code) {
  if (!(code instanceof HTMLElement) || code.closest('pre') || isInteractiveAncestor(code)) {
    return;
  }

  const text = code.textContent?.trim();
  if (!text) {
    return;
  }

  code.classList.add('copyable-inline-code');
  code.dataset.copyableCode = 'true';
  code.dataset.copyTooltip = 'Clique para copiar';
  code.setAttribute('role', 'button');
  code.setAttribute('tabindex', '0');
  code.setAttribute('aria-label', `Clique para copiar: ${text}`);
}

function enhanceCodeWithin(root) {
  if (root instanceof HTMLElement && root.matches(COPYABLE_CODE_SELECTOR)) {
    enhanceCode(root);
  }

  root.querySelectorAll?.(COPYABLE_CODE_SELECTOR).forEach(enhanceCode);
}

async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';
  document.body.appendChild(textarea);
  textarea.select();

  const copied = document.execCommand('copy');
  textarea.remove();

  if (!copied) {
    throw new Error('Não foi possível copiar o texto.');
  }
}

function getCopyableCode(target) {
  if (!(target instanceof Element)) {
    return null;
  }

  return target.closest('code[data-copyable-code="true"]');
}

function hasActiveSelectionInside(code) {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) {
    return false;
  }

  return Boolean(selection.anchorNode && code.contains(selection.anchorNode));
}

export default function Root({ children }) {
  const statusRef = useRef(null);

  useEffect(() => {
    enhanceCodeWithin(document);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement) {
            enhanceCodeWithin(node);
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const markCopied = (code, text) => {
      const previousTimer = Number(code.dataset.copyResetTimer);
      if (previousTimer) {
        window.clearTimeout(previousTimer);
      }

      code.dataset.copyState = 'copied';
      code.dataset.copyTooltip = 'Copiado!';
      code.setAttribute('aria-label', `Copiado: ${text}`);

      if (statusRef.current) {
        statusRef.current.textContent = `Copiado: ${text}`;
      }

      const timer = window.setTimeout(() => {
        delete code.dataset.copyState;
        delete code.dataset.copyResetTimer;
        code.dataset.copyTooltip = 'Clique para copiar';
        code.setAttribute('aria-label', `Clique para copiar: ${text}`);
      }, 1600);

      code.dataset.copyResetTimer = String(timer);
    };

    const copyCode = async (code) => {
      const text = code.textContent?.trim();
      if (!text) {
        return;
      }

      try {
        await copyText(text);
        markCopied(code, text);
      } catch (error) {
        console.error('Falha ao copiar código:', error);
        code.dataset.copyState = 'error';
        code.dataset.copyTooltip = 'Não foi possível copiar';

        window.setTimeout(() => {
          delete code.dataset.copyState;
          code.dataset.copyTooltip = 'Clique para copiar';
        }, 1600);
      }
    };

    const handleClick = (event) => {
      const code = getCopyableCode(event.target);
      if (!code || hasActiveSelectionInside(code)) {
        return;
      }

      void copyCode(code);
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      const code = getCopyableCode(event.target);
      if (!code) {
        return;
      }

      event.preventDefault();
      void copyCode(code);
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      observer.disconnect();
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      {children}
      <ScrollToTop />
      <span ref={statusRef} className="code-copy-status" aria-live="polite" aria-atomic="true" />
    </>
  );
}
